import { QuickNavigationOption } from "config/quickNavigationOptions";
import { DEFAULT_ZOOM_HOURS, defaultZoomLevel } from "config/zoomConfig";
import deepmerge from "deepmerge";
import omit from "lodash/omit";
import pick from "lodash/pick";
import React, { Component } from "react";
import DateTime from "utils/DateTime";
import LocalStorage from "utils/LocalStorage";
import SessionStorage from "utils/SessionStorage";

const UIStateContext = React.createContext({});

const DEFAULT_UI_STATE = {
  scheduler: {
    dataTimeRange: {
      endDate: DateTime.now().startOf("day").add(DEFAULT_ZOOM_HOURS, "hours").format(),
      startDate: DateTime.now().startOf("day").format(),
    },
    quickNavigationOption: QuickNavigationOption.TODAY,
    visibleTimeRange: {
      endDate: DateTime.now().startOf("day").add(DEFAULT_ZOOM_HOURS, "hours").format(),
      startDate: DateTime.now().startOf("day").format(),
    },
    zoomLevel: defaultZoomLevel,
  },
  selectedWorkBlock: {
    id: null,
    selectedAt: null,
  },
  sidebarOpen: true,
  slideOutState: {
    mode: "none",
    workOrderCode: null,
    workOrderId: null,
  },
  workBlockModal: {
    productionDetailsOpen: false,
  },
  workOrderDetailsSectionsOpen: {
    itemDetails: true,
    productionInformation: true,
    scheduledBlocks: true,
    workOrderInformation: true,
  },
};

const SESSIONS_STORAGE_VALUES = ["scheduler.visibleTimeRange", "scheduler.dataTimeRange"];

export class UIStateProvider extends Component {
  constructor(props) {
    super(props);

    const { sessionIdentifier } = props;
    this.dataStoreKey = `${sessionIdentifier}-uiState`;

    this.localStorage = new LocalStorage();
    this.sessionStorage = new SessionStorage();

    this.state = deepmerge.all([
      DEFAULT_UI_STATE,
      omit(this.localStorage.read(this.dataStoreKey), SESSIONS_STORAGE_VALUES),
      pick(this.sessionStorage.read(this.dataStoreKey), SESSIONS_STORAGE_VALUES),
    ]);

    this.currentState = this.state;
  }

  // Fetches current application state synchronously.
  // This is useful if you need to access current application state before setState async updates
  getCurrentState = () => this.currentState;

  updateState = (newState) => {
    const oldState = this.currentState;

    this.currentState = {
      ...oldState,
      ...newState,
    };

    this.localStorage.write(this.dataStoreKey, this.currentState);
    this.sessionStorage.write(this.dataStoreKey, this.currentState);

    this.setState(this.currentState);
  };

  contextValue = () => ({ getState: this.getCurrentState, setState: this.updateState, state: this.state });

  render() {
    return <UIStateContext.Provider value={this.contextValue()}>{this.props.children}</UIStateContext.Provider>;
  }
}

export default UIStateContext;
